import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { getRateUnit, setRateUnit, type RateUnit } from "../lib/storage";
import { useUnits } from "../hooks/useUnits";
import { fmtWeight, weightParts, type UnitSystem } from "../lib/units";
import { paceVsAvgPct } from "../lib/engage";

// Scale-cam overlay — React port of legacy static/js/live-scale.js (read-only
// reference for the UX: big rate number, unit cycling, stats row, preview
// thumb, status states). AI is off in this environment, so /api/live/:id/
// frame ALWAYS answers {readable:false,...} for a real frame — this overlay
// is written to be honest about that (the "unreadable" state says so) rather
// than implying a camera/glare problem the worker could fix by re-aiming.
// The Playwright spec (e2e/scale-cam.spec.ts) drives every OTHER state
// (reading/rejected/cooldown) by stubbing the /frame response at the network
// layer via page.route, since there's no way to make AI actually read a
// number in this environment.

const FRAME_EVERY_MS = 15000; // cadence once running (task-specified)
const FIRST_FRAME_DELAY_MS = 1500; // fast first read, mirrors legacy's 2.5s

type OverlayState = "starting" | "waiting" | "reading" | "rejected" | "cooldown" | "unreadable" | "blocked" | "error";

const UNITS: RateUnit[] = ["gpm", "ozhr", "lbhr"];
const UNIT_LABEL: Record<RateUnit, string> = { gpm: "g/min", ozhr: "oz/hr", lbhr: "lb/hr" };
const DOT_KIND: Record<OverlayState, "ok" | "warn" | "err" | "wait"> = {
  starting: "wait", waiting: "wait", reading: "ok", rejected: "warn",
  cooldown: "ok", unreadable: "warn", blocked: "err", error: "err",
};

type FrameResp = {
  readable: boolean; rejected: boolean; grams: number | null; total_g: number;
  gpm: number | null; oz_hr: number | null; lb_hr: number | null; elapsed_min: number; samples: number;
};

type WakeLockLike = { release(): Promise<void> };

// Metric keeps today's behaviour (device pref, default g/min). Imperial orgs default
// to oz/hr; a stored lb/hr or oz/hr device pref still wins, a stale g/min does not.
function defaultRateUnit(system: UnitSystem): RateUnit {
  const stored = getRateUnit();
  if (system === "imperial") return stored === "gpm" ? "ozhr" : stored;
  return stored;
}

function fmtElapsed(min: number): string {
  const m = Math.floor(min);
  const s = Math.round((min - m) * 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function ScaleCamOverlay({
  liveId, onClose, onCancelled, avgGpm = null,
}: { liveId: number; onClose: () => void; onCancelled: () => void; avgGpm?: number | null }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const wakeLockRef = useRef<WakeLockLike | null>(null);
  const intervalRef = useRef<number | null>(null);
  const firstTimeoutRef = useRef<number | null>(null);
  const inflightRef = useRef(false);
  const lastRef = useRef<FrameResp | null>(null);

  const [state, setState] = useState<OverlayState>("starting");
  const [statusText, setStatusText] = useState("starting camera…");
  const system = useUnits();
  const systemRef = useRef(system);
  systemRef.current = system;
  const pickedRef = useRef(false);
  const [unit, setUnit] = useState<RateUnit>(() => defaultRateUnit(system));
  // Org settings can land after the overlay mounts; follow them until the worker taps.
  useEffect(() => {
    if (!pickedRef.current) setUnit(defaultRateUnit(system));
  }, [system]);
  const [last, setLast] = useState<FrameResp | null>(null);
  const [big, setBig] = useState(false);

  useEffect(() => {
    document.body.classList.add("scale-open");
    return () => document.body.classList.remove("scale-open");
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function captureAndSend() {
      if (inflightRef.current) return;
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;

      const maxDim = 1024;
      const ratio = Math.min(1, maxDim / Math.max(video.videoWidth, video.videoHeight, 1));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(video.videoWidth * ratio) || video.videoWidth || 1;
      canvas.height = Math.round(video.videoHeight * ratio) || video.videoHeight || 1;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.75));
      if (!blob) return;

      inflightRef.current = true;
      setState("waiting");
      setStatusText("reading scale…");
      try {
        const fd = new FormData();
        fd.append("photo", blob, "frame.jpg");
        const res = await api.postMultipart(`/api/live/${liveId}/frame`, fd);
        if (res.status === 429) {
          setState("cooldown");
          setStatusText("cooldown");
          return;
        }
        const body = await res.json().catch(() => ({}) as Partial<FrameResp> & { error?: string; cancelled?: boolean });
        if (res.status === 409 && (body as { cancelled?: boolean }).cancelled) {
          onCancelled();
          return;
        }
        if (!res.ok) {
          setState("error");
          setStatusText((body as { error?: string }).error || `Error ${res.status}`);
          return;
        }
        const j = body as FrameResp;
        // Keep the last ACCEPTED reading on the "on scale" tile — a rejected
        // or unreadable frame shouldn't flash garbage there (legacy parity).
        const keepGrams = (j.rejected || j.grams == null) && lastRef.current ? lastRef.current.grams : j.grams;
        const merged = { ...j, grams: keepGrams };
        lastRef.current = merged;
        setLast(merged);
        if (j.readable && j.rejected) {
          setState("rejected");
          setStatusText(`Ignored implausible read (${j.grams != null ? fmtWeight(j.grams, systemRef.current, 1) : "?"})`);
        } else if (j.readable) {
          setState("reading");
          setStatusText(`Read ${j.grams != null ? fmtWeight(j.grams, systemRef.current, 1) : ""}`);
          if (navigator.vibrate) navigator.vibrate(10);
        } else {
          // AI is off in this environment — {readable:false} is the ONLY
          // shape any contract target ever returns. Say so plainly rather
          // than "check aim/glare", which would blame a camera problem the
          // worker can't actually fix.
          setState("unreadable");
          setStatusText("Display unreadable — AI off in this environment");
        }
      } catch {
        setState("error");
        setStatusText("Network error");
      } finally {
        inflightRef.current = false;
      }
    }

    (async () => {
      const nav = navigator as Navigator & {
        mediaDevices?: { getUserMedia(c: MediaStreamConstraints): Promise<MediaStream> };
      };
      const mediaDevices = nav.mediaDevices;
      if (!mediaDevices?.getUserMedia) {
        setState("blocked");
        setStatusText("This browser doesn't support camera capture.");
        return;
      }
      let stream: MediaStream;
      try {
        stream = await mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
      } catch {
        if (!cancelled) {
          setState("blocked");
          setStatusText("Camera blocked — allow camera access for this site");
        }
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setState("waiting");
      setStatusText("camera on — first read shortly");

      try {
        const wl = await (navigator as Navigator & { wakeLock?: { request(t: "screen"): Promise<WakeLockLike> } })
          .wakeLock?.request("screen");
        wakeLockRef.current = wl ?? null;
      } catch {
        /* best-effort */
      }

      firstTimeoutRef.current = window.setTimeout(captureAndSend, FIRST_FRAME_DELAY_MS);
      intervalRef.current = window.setInterval(captureAndSend, FRAME_EVERY_MS);
    })();

    return () => {
      cancelled = true;
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      if (firstTimeoutRef.current) window.clearTimeout(firstTimeoutRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      wakeLockRef.current?.release().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveId]);

  function cycleUnit() {
    const next = UNITS[(UNITS.indexOf(unit) + 1) % UNITS.length];
    pickedRef.current = true;
    setUnit(next);
    setRateUnit(next);
  }

  const rate = (() => {
    if (!last || last.gpm == null) return "—";
    if (unit === "gpm") return last.gpm.toFixed(1);
    if (unit === "ozhr") return last.oz_hr != null ? last.oz_hr.toFixed(1) : "—";
    return last.lb_hr != null ? last.lb_hr.toFixed(2) : "—";
  })();

  // M6 pace-vs-self, recomputed live every frame from the CURRENT reading —
  // unlike TrimPage's timer-step pill (a static snapshot from trim start),
  // this one tracks the worker's pace as it changes through the run. Never
  // shows another worker's pace — avgGpm is this player's own cached
  // average, passed down from TrimPage's engage/me snapshot.
  const pace = paceVsAvgPct(last?.gpm ?? null, avgGpm);

  return (
    <div className="scale-overlay" data-testid="scale-overlay" data-state={state}>
      <div className="scale-head">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Back to timer</button>
        <div className="scale-status" data-testid="scale-status">
          <span className={`dot ${DOT_KIND[state]}`} />
          <span>{statusText}</span>
        </div>
      </div>

      <div className="scale-main" onClick={cycleUnit}>
        <div className="scale-rate" data-testid="scale-rate">{rate}</div>
        <div className="scale-unit">{UNIT_LABEL[unit]} · tap to switch</div>
      </div>

      {pace != null && (
        <div
          className={`pace-pill ${pace >= 0 ? "up" : "down"}`}
          data-testid="scale-pace-pill"
          data-direction={pace >= 0 ? "up" : "down"}
        >
          {pace >= 0 ? "▲" : "▼"} {Math.abs(pace)}% vs your avg
        </div>
      )}

      <div className="scale-stats">
        <div className="scale-stat"><div className="v">{weightParts(last?.total_g ?? 0, system, 1).value}</div><div className="l">total {weightParts(0, system).unit}</div></div>
        <div className="scale-stat"><div className="v">{last?.grams != null ? fmtWeight(last.grams, system, 1) : "—"}</div><div className="l">on scale</div></div>
        <div className="scale-stat"><div className="v">{fmtElapsed(last?.elapsed_min ?? 0)}</div><div className="l">elapsed</div></div>
        <div className="scale-stat"><div className="v">{last?.samples ?? 0}</div><div className="l">reads</div></div>
      </div>

      <div className={`scale-preview${big ? " big" : ""}`} onClick={() => setBig((b) => !b)}>
        <video ref={videoRef} autoPlay playsInline muted />
        <div className="scale-preview-hint">aim at the scale display</div>
      </div>

      <div className="scale-foot muted small">
        Prop the phone so the back camera sees the scale readout. A reading is taken every {Math.round(FRAME_EVERY_MS / 1000)}s.
      </div>
    </div>
  );
}
